import * as React from "react";
import { Info } from "lucide-react";

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { MarketDataConnectOption } from "@/types/marketDataAttribution";
import {
  MARKET_DATA_PREFERENCE_SETTINGS_PATH,
  MARKET_DATA_RESOLVE_SOURCE_CTA_LABEL,
  type MarketDataSourceConnectAction,
} from "@/utils/marketDataConnectGate";
import { humanizeMarketDataBrokerSlug } from "@/utils/marketDataSourceChoice";
import { supportsInlineMarketDataBrokerReconnect } from "@/utils/marketDataBrokerReconnect";

export type MarketDataConnectGateNoticeProps = {
  message: string;
  action?: string;
  /** BE hint only — CTA always routes to instrument market data settings. */
  connectOptions?: MarketDataConnectOption[];
  tradeAnalyticsAvailable?: boolean;
  title?: string;
  className?: string;
  resolveCtaLabel?: string;
  onNavigate: (href: string) => void;
};

/** Shared OHLC-unavailable callout — one CTA to instrument market data (reconnect or switch). */
export function MarketDataConnectGateNotice({
  message,
  action,
  tradeAnalyticsAvailable,
  title = "Chart data unavailable",
  className,
  resolveCtaLabel = MARKET_DATA_RESOLVE_SOURCE_CTA_LABEL,
  onNavigate,
}: MarketDataConnectGateNoticeProps): React.ReactElement {
  return (
    <Alert variant="warning" data-market-data-connect-gate="" className={className}>
      <Info aria-hidden />
      <AlertTitle className="font-[family-name:var(--font-primary)] text-sm font-medium leading-5 text-foreground">
        {title}
      </AlertTitle>
      <AlertDescription className="group-has-[>svg]/alert:col-start-2 space-y-3 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white">
        <span className="block">{message}</span>
        {action ? <span className="block text-xs opacity-90">{action}</span> : null}
        {tradeAnalyticsAvailable ? (
          <span className="block text-xs opacity-90">
            Trade stats and lists still work — only OHLC charts need a fix.
          </span>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={() => onNavigate(MARKET_DATA_PREFERENCE_SETTINGS_PATH)}
          >
            {resolveCtaLabel}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

export type MarketDataSourceConnectPromptProps = {
  action: MarketDataSourceConnectAction;
  symbol?: string;
  /** Opens inline reconnect when supported; otherwise navigates away. */
  onStartReconnect?: () => void;
  onNavigate?: (href: string) => void;
  className?: string;
};

function brokerDisplayLabel(action: MarketDataSourceConnectAction): string {
  return (
    humanizeMarketDataBrokerSlug(action.broker) ??
    (action.label.replace(/^Connect\s+/i, "").trim() || "Broker")
  );
}

/** Branded inline callout when a switchable OHLC lane needs broker reconnect. */
export function MarketDataSourceConnectPrompt({
  action,
  symbol,
  onStartReconnect,
  onNavigate,
  className,
}: MarketDataSourceConnectPromptProps): React.ReactElement {
  const brokerLabel = brokerDisplayLabel(action);
  const symbolPart = symbol?.trim() ? ` for ${symbol.trim()}` : "";
  const inlineReconnect = supportsInlineMarketDataBrokerReconnect(action.broker);

  return (
    <Alert
      variant="info"
      data-market-data-source-connect=""
      className={cn(BROKER_ALERT_CLASS, "!gap-y-3 px-3 py-3", className)}
    >
      <BrokerAlertIcon tone="info" />
      <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>
        {brokerLabel} not connected
      </AlertTitle>
      <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <p className="m-0 min-w-0 flex-1 leading-5">
            {inlineReconnect
              ? `Reconnect ${brokerLabel}${symbolPart} here — verify login, consent once, and confirm OHLC without leaving this page.`
              : `Open Broker Management to reconnect ${brokerLabel}${symbolPart}, then return here to confirm this OHLC source.`}
          </p>
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="h-8 w-full shrink-0 px-5 text-[0.75rem] normal-case tracking-normal sm:w-auto"
            onClick={() =>
              inlineReconnect && onStartReconnect
                ? onStartReconnect()
                : onNavigate?.(action.href)
            }
          >
            {action.label}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
